import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import {
  getReadingListsShelf,
  ReadingListsShelfCounts,
  ReadingListsShelfDirection,
  ReadingListsShelfFilter,
  ReadingListsShelfSort,
  List,
} from "../../../api/lists";
import { useAccountData } from "../../../hooks/useAccountData";

export const READING_LISTS_SHELF_QUERY_KEY = ["reading-lists-shelf"] as const;

const PAGE_SIZE = 20;

export interface UseReadingListsShelfArgs {
  filter?: ReadingListsShelfFilter;
  q?: string;
  sort?: ReadingListsShelfSort;
  direction?: ReadingListsShelfDirection;
}

// Read-only hook backing the shelf's lists rail. Mutations (delete /
// unfollow / cover upload) live on the detail pane now that the rail
// kebab is gone — there's no rail-level affordance that needs them, so
// keeping them here would just be dead surface. Detail pane fires its
// own mutations directly against the api/lists.ts module and invalidates
// READING_LISTS_SHELF_QUERY_KEY when it lands a mutation that should
// reflect on the rail.
export function useReadingListsShelf(args: UseReadingListsShelfArgs = {}) {
  const accountData = useAccountData();
  const userId = accountData?.user?.id;

  const filter = args.filter ?? "all";
  const q = args.q ?? "";
  const sort = args.sort ?? "created_time";
  const direction = args.direction ?? "desc";

  const queryKey = [
    ...READING_LISTS_SHELF_QUERY_KEY,
    userId,
    filter,
    q,
    sort,
    direction,
  ] as const;

  const query = useInfiniteQuery({
    queryKey,
    // counts are only requested on page=1 — subsequent pages don't need them
    // and we don't want to pay 3× COUNT(*) on every infinite-scroll fetch.
    queryFn: ({ pageParam = 1 }) =>
      getReadingListsShelf({
        page: pageParam,
        perPage: PAGE_SIZE,
        filter,
        q,
        sort,
        direction,
        includeCounts: pageParam === 1,
      }).then((res) => ({
        ...res.data.payload,
        counts: res.data.meta.counts,
      })),
    initialPageParam: 1,
    getNextPageParam: ({ pagination }) =>
      pagination.page < pagination.total_pages
        ? pagination.page + 1
        : undefined,
    // Hold the prior (filter, q) results across key changes so typing in the
    // rail's search doesn't drop the rail (or the screen) into a loading
    // state on every keystroke.
    placeholderData: keepPreviousData,
    // 60s freshness window — the shelf is the entry point users return to
    // most often (open a list, read, hit back), and react-query's default
    // staleTime of 0 was firing a fresh /v1/lists/ request on every remount.
    // Refetches that came back identical still re-rendered the row tree,
    // which interrupted in-flight image paints and produced the visible
    // "covers flickering on return" symptom. Mutations across the lists
    // surface (create / delete / unfollow / cover upload / add-to-list)
    // already invalidate READING_LISTS_SHELF_QUERY_KEY explicitly, so we
    // don't lose freshness on the cases that matter — this just stops the
    // "navigate away and come back" refetch.
    staleTime: 60_000,
    enabled: userId != null,
  });

  const lists: List[] = useMemo(() => {
    const seen = new Set<number>();
    const out: List[] = [];
    for (const page of query.data?.pages ?? []) {
      for (const list of page.results) {
        if (seen.has(list.id)) continue;
        seen.add(list.id);
        out.push(list);
      }
    }
    return out;
  }, [query.data]);

  const totalResults = query.data?.pages[0]?.pagination.total_results ?? 0;
  const counts: ReadingListsShelfCounts | undefined =
    query.data?.pages[0]?.counts;

  return {
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
    userId,
    lists,
    totalResults,
    counts,
  };
}
